import { FC, FormEvent, ReactNode, useEffect, useState } from 'react';
import { PencilIcon } from '@heroicons/react/outline';

import {
  MailSequenceEnrollmentDataFragment,
  MailSequenceReferenceDateType,
  useMailSequenceUpdateEnrollmentNameMutation,
} from '../../../../generated/graphql';

import ActionButton from '../../../components/ActionButton';
import ActionButtonGroup from '../../../components/ActionButtonGroup';
import Button from '../../../components/Button';
import InputGroup from '../../../components/InputGroup';
import { customToast } from '../../../components/ToastAlert/customToast';
import { sequenceDisplayTitle } from '../../../lib/mailSequence';
import { formatProgramDate } from '../../ScheduleMail/anchorDates';
import MailStepBadge from '../../../components/MailStepBadge';
import { usePatientProfileContext } from '..';

interface Props {
  enrollment: MailSequenceEnrollmentDataFragment;
  headerActions?: ReactNode;
  onNameUpdated?: () => void;
}

const SequenceSummary: FC<Props> = ({
  enrollment,
  headerActions,
  onNameUpdated,
}) => {
  const { patient } = usePatientProfileContext();
  const formattedDate =
    formatProgramDate(enrollment.referenceDate) ??
    enrollment.referenceDate.slice(0, 10);
  const sendTime = enrollment.defaultSendTime?.slice(0, 5) ?? '';
  const title = sequenceDisplayTitle(enrollment);
  const firstName = patient?.firstName?.trim() || 'the client';

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(enrollment.name?.trim() ?? '');

  useEffect(() => {
    if (!isEditingName) {
      setNameDraft(enrollment.name?.trim() ?? '');
    }
  }, [enrollment.name, isEditingName]);

  const [updateName, { loading: savingName }] =
    useMailSequenceUpdateEnrollmentNameMutation({
      onCompleted: (data) => {
        if (data.mailSequenceUpdateEnrollmentName?.success) {
          setIsEditingName(false);
          onNameUpdated?.();
          return;
        }
        customToast.error(
          data.mailSequenceUpdateEnrollmentName?.error ||
            'Could not update sequence name.',
        );
      },
      onError: (err) =>
        customToast.error(err.message || 'Could not update sequence name.'),
    });

  const stepIndexes = enrollment.scheduledSends.map((s) => s.stepIndex);
  const firstStep = stepIndexes.length ? Math.min(...stepIndexes) : 0;
  const lastStep = stepIndexes.length ? Math.max(...stepIndexes) : 0;
  const isSingleStep = firstStep === lastStep;

  const isProgramDate =
    enrollment.referenceDateType ===
      MailSequenceReferenceDateType.ProgramStartDate ||
    enrollment.referenceDateType ===
      MailSequenceReferenceDateType.ProgramEndDate;
  const programDateNoun =
    enrollment.referenceDateType ===
    MailSequenceReferenceDateType.ProgramEndDate
      ? 'program end date'
      : 'program start date';

  const stepCircleClass =
    'inline-flex h-4 w-4 shrink-0 items-center justify-center self-center rounded-full bg-secondary-100 text-[10px] font-bold leading-none text-white';

  const mailRange = isSingleStep ? (
    <MailStepBadge stepIndex={firstStep} />
  ) : (
    <span className="inline-flex items-baseline gap-1 font-medium">
      <span>Mail</span>
      <span className={stepCircleClass} aria-label={`Mail ${firstStep + 1}`}>
        {firstStep + 1}
      </span>
      <span className="font-normal">to</span>
      <span className={stepCircleClass} aria-label={`Mail ${lastStep + 1}`}>
        {lastStep + 1}
      </span>
    </span>
  );

  const timingClause = isProgramDate
    ? `is scheduled from ${firstName}'s ${programDateNoun} (${formattedDate})`
    : `is scheduled from ${formattedDate}`;

  const sendClause = sendTime
    ? ` and will be sent at ${sendTime} on each date in the steps below`
    : '';

  const handleSaveName = (e?: FormEvent) => {
    e?.preventDefault();
    updateName({
      variables: {
        enrollmentId: enrollment.id,
        name: nameDraft.trim() || null,
      },
    });
  };

  return (
    <div className="w-full">
      <div className="mb-4 flex w-full flex-row items-start justify-between gap-x-6">
        <div className="flex min-w-0 flex-1 flex-col gap-y-2">
          {isEditingName ? (
            <form
              className="flex w-full max-w-xl flex-col gap-y-2"
              onSubmit={handleSaveName}
            >
              <InputGroup
                type="text"
                label="Sequence name"
                useNaturalLettering
                inputSize="extra-small"
                containerClassName="w-full"
                placeholder="Leave blank for default title"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                maxLength={255}
                autoFocus
              />
              <div className="flex flex-row items-center gap-x-2">
                <Button
                  type="submit"
                  title={savingName ? 'Saving...' : 'Save'}
                  theme="primary"
                  size="small"
                  disabled={savingName}
                />
                <Button
                  type="button"
                  title="Cancel"
                  theme="secondary"
                  size="small"
                  disabled={savingName}
                  onClick={() => {
                    setIsEditingName(false);
                    setNameDraft(enrollment.name?.trim() ?? '');
                  }}
                />
              </div>
            </form>
          ) : (
            <span className="min-w-0 text-body font-medium text-secondary-150">
              {title}
            </span>
          )}
        </div>
        {!isEditingName ? (
          <ActionButtonGroup size="small" className="shrink-0">
            <ActionButton
              size="small"
              aria-label="Edit sequence name"
              title="Edit sequence name"
              onClick={() => setIsEditingName(true)}
            >
              <PencilIcon className="h-4 w-4 text-neutral-125" />
            </ActionButton>
            {headerActions}
          </ActionButtonGroup>
        ) : headerActions ? (
          <ActionButtonGroup size="small" className="shrink-0">
            {headerActions}
          </ActionButtonGroup>
        ) : null}
      </div>

      <p className="text-caption text-neutral-125">
        {mailRange} {timingClause}
        {sendClause}.
      </p>
    </div>
  );
};

export default SequenceSummary;
